// Kolonie na Keplerovi: pravidla. Čistá logika bez časovačů a sítě, náhoda jen ze seedu ve stavu.
// Hráči se uvnitř adresují indexem (pořadí tahu); ven (události `to`, pending) jdou jejich id.
import { nextRandom, shuffle } from "../../shared/rng.ts";
import type { ApplyResult, GameEvent, GameModule, GameOptions, PlayerRef } from "../types.ts";
import {
  BANK_PER_RES,
  COSTS,
  DEV_COUNTS,
  LIMITS,
  PORT_TYPES,
  RESOURCES,
  TERRAIN_COUNTS,
  TOKENS,
  type Bag,
  type Building,
  type Dev,
  type KolonieAction,
  type KolonieView,
  type Pending,
  type Phase,
  type Res,
  type Terrain,
  type TradeOffer,
} from "./meta.ts";
import { TOPOLOGY as T, otherEnd } from "./topology.ts";
import { botAction, type BotMemory } from "./bot.ts";

export const STATE_VERSION = 1;

export interface KolonieState {
  version: number;
  hexes: { terrain: Terrain; number: number | null }[];
  ports: { edge: number; type: Res | "any" }[];
  robber: number;
  corners: (Building | null)[];
  edges: (number | null)[];
  players: PlayerRef[];
  hands: Record<Res, number>[];
  bank: Record<Res, number>;
  /** Balíček výzkumu, vrchní karta je poslední. */
  devDeck: Dev[];
  devHands: { kind: Dev; boughtTurn: number }[][];
  knights: number[];
  turn: number;
  /** Pořadové číslo tahu (od 1), pro pravidlo „ne karta koupená v tomtéž tahu“. */
  turnCount: number;
  phase: Phase;
  /** Rozestavění: krok v pořadí „had“ a co se právě staví. */
  setup: { step: number; placing: "settlement" | "road"; lastCorner: number } | null;
  dice: [number, number] | null;
  /** Kolik karet musí kdo ještě odhodit (index hráče → počet). */
  discard: number[];
  /** Kam se vrátit po pirátech nebo po stavbě 2 tunelů zdarma. */
  returnPhase: Phase | null;
  stealFrom: number[];
  freeRoads: number;
  devPlayedThisTurn: boolean;
  longestHolder: number | null;
  largestHolder: number | null;
  roadLengths: number[];
  trade: TradeOffer | null;
  nextOfferId: number;
  targetPoints: number;
  winner: number | null;
  seed: number;
}

type R<S> = ApplyResult<S>;
/** Stačí mapa (stav i pohled hráče ji mají stejnou), aby šly kontroly použít i v botech a klientovi. */
type Board = Pick<KolonieState, "corners" | "edges" | "ports">;
const fail = (error: string): R<KolonieState> => ({ ok: false, error });

const emptyHand = (): Record<Res, number> => ({ kov: 0, sklo: 0, voda: 0, jidlo: 0, energie: 0 });
const total = (h: Bag) => RESOURCES.reduce((n, r) => n + (h[r] ?? 0), 0);
const has = (h: Record<Res, number>, cost: Bag) => RESOURCES.every((r) => h[r] >= (cost[r] ?? 0));

function rand(s: KolonieState, n: number): { value: number; state: KolonieState } {
  const r = nextRandom(s.seed);
  return { value: Math.floor(r.value * n), state: { ...s, seed: r.seed } };
}

// ---------- Příprava ----------

function generateMap(seed: number) {
  const terrains: Terrain[] = [];
  for (const [t, n] of Object.entries(TERRAIN_COUNTS)) for (let i = 0; i < n; i++) terrains.push(t as Terrain);
  let s = seed;
  // Opakujeme míchání, dokud 6 a 8 neleží vedle sebe (oficiální pravidlo pro náhodné rozložení).
  for (let attempt = 0; attempt < 5000; attempt++) {
    const t = shuffle(terrains, s);
    const k = shuffle(TOKENS, t.seed);
    s = k.seed;
    let ti = 0;
    const hexes = t.items.map((terrain) => ({ terrain, number: terrain === "krater" ? null : k.items[ti++] }));
    const red = (h: number) => hexes[h].number === 6 || hexes[h].number === 8;
    const ok = T.hexes.every((h, i) => !red(i) || h.neighbors.every((nb) => !red(nb)));
    if (ok) {
      const p = shuffle(PORT_TYPES, s);
      return { hexes, ports: T.portEdges.map((edge, i) => ({ edge, type: p.items[i] })), seed: p.seed };
    }
  }
  throw new Error("Nepodařilo se rozložit mapu");
}

function init(options: GameOptions, players: PlayerRef[], seed: number): KolonieState {
  const map = generateMap(seed);
  let s = map.seed;
  // Začínající hráč náhodně, pořadí kolem stolu zůstane.
  const start = nextRandom(s);
  s = start.seed;
  const first = Math.floor(start.value * players.length);
  const order = [...players.slice(first), ...players.slice(0, first)].map((p) => ({ ...p }));
  const deck: Dev[] = [];
  for (const [d, n] of Object.entries(DEV_COUNTS)) for (let i = 0; i < n; i++) deck.push(d as Dev);
  const shuffled = shuffle(deck, s);
  const n = order.length;
  return {
    version: STATE_VERSION,
    hexes: map.hexes,
    ports: map.ports,
    robber: map.hexes.findIndex((h) => h.terrain === "krater"),
    corners: T.corners.map(() => null),
    edges: T.edges.map(() => null),
    players: order,
    hands: order.map(emptyHand),
    bank: { kov: BANK_PER_RES, sklo: BANK_PER_RES, voda: BANK_PER_RES, jidlo: BANK_PER_RES, energie: BANK_PER_RES },
    devDeck: shuffled.items,
    devHands: order.map(() => []),
    knights: new Array(n).fill(0),
    turn: 0,
    turnCount: 0,
    phase: "setup",
    setup: { step: 0, placing: "settlement", lastCorner: -1 },
    dice: null,
    discard: new Array(n).fill(0),
    returnPhase: null,
    stealFrom: [],
    freeRoads: 0,
    devPlayedThisTurn: false,
    longestHolder: null,
    largestHolder: null,
    roadLengths: new Array(n).fill(0),
    trade: null,
    nextOfferId: 1,
    targetPoints: options.points === "8" ? 8 : 10,
    winner: null,
    seed: shuffled.seed,
  };
}

/** Pořadí rozestavění „had“: 0,1,2,3,3,2,1,0. */
function setupPlayer(s: KolonieState, step: number): number {
  const n = s.players.length;
  return step < n ? step : 2 * n - 1 - step;
}

// ---------- Dotazy na mapu ----------

function piecesUsed(s: KolonieState, p: number) {
  let tunel = 0;
  let zakladna = 0;
  let mesto = 0;
  for (const e of s.edges) if (e === p) tunel++;
  for (const c of s.corners) if (c?.owner === p) c.city ? mesto++ : zakladna++;
  return { tunel, zakladna, mesto };
}

/** Pravidlo vzdálenosti: roh je volný a žádný sousední roh není obsazený. */
export function distanceOk(s: Board, corner: number): boolean {
  return s.corners[corner] === null && T.corners[corner].corners.every((c) => s.corners[c] === null);
}

/** Tunel smí na volnou hranu, která navazuje na mou stavbu nebo můj tunel (přes roh bez cizí stavby). */
export function roadOk(s: Board, p: number, edge: number): boolean {
  if (s.edges[edge] !== null) return false;
  return T.edges[edge].corners.some((c) => {
    const b = s.corners[c];
    if (b && b.owner === p) return true;
    if (b && b.owner !== p) return false;
    return T.corners[c].edges.some((e) => e !== edge && s.edges[e] === p);
  });
}

export function settlementOk(s: Board, p: number, corner: number): boolean {
  return distanceOk(s, corner) && T.corners[corner].edges.some((e) => s.edges[e] === p);
}

export function legalRoads(s: Board, p: number): number[] {
  return T.edges.map((_, i) => i).filter((e) => roadOk(s, p, e));
}

export function legalSettlements(s: Board, p: number): number[] {
  return T.corners.map((_, i) => i).filter((c) => settlementOk(s, p, c));
}

export function legalSetupSettlements(s: KolonieState): number[] {
  return T.corners.map((_, i) => i).filter((c) => distanceOk(s, c));
}

export function legalSetupRoads(s: KolonieState): number[] {
  if (!s.setup) return [];
  return T.corners[s.setup.lastCorner]?.edges.filter((e) => s.edges[e] === null) ?? [];
}

/** Směnný kurz hráče u banky pro danou surovinu (4, 3 nebo 2 podle stanic). */
export function bankRate(s: Board, p: number, res: Res): number {
  let rate = 4;
  for (const port of s.ports) {
    const owns = T.edges[port.edge].corners.some((c) => s.corners[c]?.owner === p);
    if (!owns) continue;
    if (port.type === res) return 2;
    if (port.type === "any") rate = 3;
  }
  return rate;
}

/** Nejdelší souvislý tah tunelů hráče (hrany se neopakují, cizí stavba tah přeruší). */
export function longestRoad(s: KolonieState, p: number): number {
  const mine = s.edges.map((o, i) => (o === p ? i : -1)).filter((i) => i >= 0);
  if (!mine.length) return 0;
  const used = new Set<number>();
  const blocked = (c: number) => s.corners[c] !== null && s.corners[c]!.owner !== p;
  const dfs = (corner: number): number => {
    let best = 0;
    for (const e of T.corners[corner].edges) {
      if (s.edges[e] !== p || used.has(e)) continue;
      used.add(e);
      const next = otherEnd(e, corner);
      best = Math.max(best, 1 + (blocked(next) ? 0 : dfs(next)));
      used.delete(e);
    }
    return best;
  };
  let best = 0;
  const starts = new Set(mine.flatMap((e) => T.edges[e].corners));
  for (const c of starts) best = Math.max(best, dfs(c));
  return best;
}

export function points(s: KolonieState, p: number, includeHidden = true): number {
  let pts = 0;
  for (const c of s.corners) if (c?.owner === p) pts += c.city ? 2 : 1;
  if (s.longestHolder === p) pts += 2;
  if (s.largestHolder === p) pts += 2;
  if (includeHidden) pts += s.devHands[p].filter((d) => d.kind === "bod").length;
  return pts;
}

// ---------- Pomocné změny stavu ----------

function pay(s: KolonieState, p: number, cost: Bag): KolonieState {
  const hand = { ...s.hands[p] };
  const bank = { ...s.bank };
  for (const r of RESOURCES) {
    hand[r] -= cost[r] ?? 0;
    bank[r] += cost[r] ?? 0;
  }
  const hands = s.hands.slice();
  hands[p] = hand;
  return { ...s, hands, bank };
}

function gain(s: KolonieState, p: number, bag: Bag): KolonieState {
  const hand = { ...s.hands[p] };
  const bank = { ...s.bank };
  for (const r of RESOURCES) {
    hand[r] += bag[r] ?? 0;
    bank[r] -= bag[r] ?? 0;
  }
  const hands = s.hands.slice();
  hands[p] = hand;
  return { ...s, hands, bank };
}

function transfer(s: KolonieState, from: number, to: number, bag: Bag): KolonieState {
  const hands = s.hands.slice();
  hands[from] = { ...hands[from] };
  hands[to] = { ...hands[to] };
  for (const r of RESOURCES) {
    hands[from][r] -= bag[r] ?? 0;
    hands[to][r] += bag[r] ?? 0;
  }
  return { ...s, hands };
}

/** Přepočítá nejdelší tunelovou síť podle oficiálních pravidel (držitel drží při shodě). */
function updateLongest(s: KolonieState, events: GameEvent[]): KolonieState {
  const lengths = s.players.map((_, p) => longestRoad(s, p));
  const max = Math.max(...lengths);
  let holder = s.longestHolder;
  if (max < 5) holder = null;
  else if (holder !== null && lengths[holder] === max) {
    /* drží dál */
  } else {
    const best = lengths.map((l, p) => (l === max ? p : -1)).filter((p) => p >= 0);
    holder = best.length === 1 ? best[0] : null;
  }
  if (holder !== s.longestHolder) events.push({ type: "longestRoad", holder, previous: s.longestHolder, length: holder === null ? 0 : lengths[holder] });
  return { ...s, roadLengths: lengths, longestHolder: holder };
}

function updateLargest(s: KolonieState, events: GameEvent[]): KolonieState {
  const p = s.turn;
  const holder = s.largestHolder;
  if (s.knights[p] >= 3 && (holder === null || (holder !== p && s.knights[p] > s.knights[holder]))) {
    events.push({ type: "largestArmy", holder: p, previous: holder, knights: s.knights[p] });
    return { ...s, largestHolder: p };
  }
  return s;
}

/** Výhra jen na vlastním tahu. */
function checkWin(s: KolonieState, events: GameEvent[]): KolonieState {
  if (s.phase === "setup" || s.winner !== null) return s;
  const p = s.turn;
  const pts = points(s, p);
  if (pts >= s.targetPoints) {
    events.push({ type: "gameOver", winner: p, points: s.players.map((_, i) => points(s, i)) });
    return { ...s, winner: p, phase: "over", trade: null };
  }
  return s;
}

// ---------- Akce ----------

function produce(s: KolonieState, sum: number, events: GameEvent[]): KolonieState {
  const want: Record<Res, number[]> = { kov: [], sklo: [], voda: [], jidlo: [], energie: [] };
  for (const r of RESOURCES) want[r] = s.players.map(() => 0);
  s.hexes.forEach((h, i) => {
    if (h.number !== sum || i === s.robber || h.terrain === "krater") return;
    for (const c of T.hexes[i].corners) {
      const b = s.corners[c];
      if (b) want[h.terrain as Res][b.owner] += b.city ? 2 : 1;
    }
  });
  const gains: Bag[] = s.players.map(() => ({}));
  const blocked: Res[] = [];
  for (const r of RESOURCES) {
    const need = want[r].reduce((a, b) => a + b, 0);
    if (!need) continue;
    const takers = want[r].filter((n) => n > 0).length;
    if (need <= s.bank[r]) want[r].forEach((n, p) => n && (gains[p][r] = n));
    else if (takers === 1) {
      const p = want[r].findIndex((n) => n > 0);
      if (s.bank[r] > 0) gains[p][r] = s.bank[r];
    } else blocked.push(r);
  }
  let next = s;
  gains.forEach((g, p) => {
    if (total(g)) next = gain(next, p, g);
  });
  events.push({ type: "produced", sum, gains, blocked });
  return next;
}

function afterRobber(s: KolonieState, events: GameEvent[]): KolonieState {
  const phase = s.returnPhase ?? "main";
  return checkWin({ ...s, phase, returnPhase: null, stealFrom: [] }, events);
}

function doSteal(s: KolonieState, victim: number, events: GameEvent[]): KolonieState {
  const thief = s.turn;
  const cards: Res[] = RESOURCES.flatMap((r) => new Array(s.hands[victim][r]).fill(r));
  if (!cards.length) {
    events.push({ type: "stole", thief, victim, nothing: true });
    return s;
  }
  const r = rand(s, cards.length);
  const res = cards[r.value];
  const next = transfer(r.state, victim, thief, { [res]: 1 });
  events.push({ type: "stole", thief, victim });
  events.push({ type: "stoleCard", to: [s.players[thief].id, s.players[victim].id], thief, victim, res });
  return next;
}

function validBag(b: unknown): b is Bag {
  if (!b || typeof b !== "object") return false;
  return Object.entries(b).every(([k, v]) => (RESOURCES as string[]).includes(k) && Number.isInteger(v) && (v as number) >= 0);
}

function applyAction(s: KolonieState, playerId: string, action: KolonieAction): R<KolonieState> {
  if (!action || typeof action !== "object") return fail("unknown-action");
  if (s.phase === "over") return fail("game-over");
  const p = s.players.findIndex((pl) => pl.id === playerId);
  if (p < 0) return fail("not-a-player");
  const events: GameEvent[] = [];
  const current = p === s.turn;

  // --- Akce mimo tah: odhazování a odpovědi na obchod ---
  if (action.type === "discard") {
    if (s.phase !== "discard" || s.discard[p] === 0) return fail("no-discard");
    if (!validBag(action.cards) || total(action.cards) !== s.discard[p] || !has(s.hands[p], action.cards)) return fail("bad-discard");
    let next = pay(s, p, action.cards);
    const discard = next.discard.slice();
    discard[p] = 0;
    next = { ...next, discard };
    events.push({ type: "discarded", player: p, cards: action.cards });
    if (discard.every((d) => d === 0)) next = { ...next, phase: "robber" };
    return { ok: true, state: next, events };
  }
  if (action.type === "respondTrade" || action.type === "counterTrade") {
    const t = s.trade;
    if (!t || t.id !== action.offerId || current || (t.to !== null && t.to !== p)) return fail("no-offer");
    if (action.type === "respondTrade") {
      if (action.response === "accept" && !has(s.hands[p], t.want)) return fail("cannot-afford");
      if (action.response !== "accept" && action.response !== "decline") return fail("bad-response");
      const trade = { ...t, responses: { ...t.responses, [p]: { kind: action.response } } };
      events.push({ type: "tradeResponse", player: p, offerId: t.id, response: action.response });
      return { ok: true, state: { ...s, trade }, events };
    }
    if (!validBag(action.give) || !validBag(action.want) || !total(action.give) || !total(action.want)) return fail("bad-offer");
    if (RESOURCES.some((r) => (action.give[r] ?? 0) > 0 && (action.want[r] ?? 0) > 0)) return fail("same-resource");
    // Protinabídka z pohledu nabízejícího: give = co dá hráč na tahu, want = co dostane od odpovídajícího.
    if (!has(s.hands[p], action.want)) return fail("cannot-afford");
    const trade = { ...t, responses: { ...t.responses, [p]: { kind: "counter" as const, give: action.give, want: action.want } } };
    events.push({ type: "tradeResponse", player: p, offerId: t.id, response: "counter", give: action.give, want: action.want });
    return { ok: true, state: { ...s, trade }, events };
  }

  if (!current) return fail("not-your-turn");

  // --- Rozestavění ---
  if (s.phase === "setup") {
    const st = s.setup!;
    if (action.type === "placeSettlement") {
      if (st.placing !== "settlement" || !distanceOk(s, action.corner)) return fail("bad-corner");
      const corners = s.corners.slice();
      corners[action.corner] = { owner: p, city: false };
      let next: KolonieState = { ...s, corners, setup: { ...st, placing: "road", lastCorner: action.corner } };
      events.push({ type: "built", player: p, kind: "zakladna", corner: action.corner, setup: true });
      if (st.step >= s.players.length) {
        // Druhá základna: po 1 surovině z každého sousedního pole.
        const bag: Bag = {};
        for (const h of T.corners[action.corner].hexes) {
          const t = s.hexes[h].terrain;
          if (t !== "krater" && next.bank[t] - (bag[t] ?? 0) > 0) bag[t] = (bag[t] ?? 0) + 1;
        }
        next = gain(next, p, bag);
        events.push({ type: "startingResources", player: p, gains: bag });
      }
      next = updateLongest(next, events);
      return { ok: true, state: next, events };
    }
    if (action.type === "placeRoad") {
      if (st.placing !== "road" || !legalSetupRoads(s).includes(action.edge)) return fail("bad-edge");
      const edges = s.edges.slice();
      edges[action.edge] = p;
      let next: KolonieState = { ...s, edges };
      events.push({ type: "built", player: p, kind: "tunel", edge: action.edge, setup: true });
      const step = st.step + 1;
      if (step >= 2 * s.players.length) {
        next = { ...next, setup: null, phase: "roll", turn: 0, turnCount: 1 };
        events.push({ type: "setupDone" }, { type: "turn", player: 0 });
      } else {
        const turn = setupPlayer(s, step);
        next = { ...next, setup: { step, placing: "settlement", lastCorner: -1 }, turn };
        events.push({ type: "turn", player: turn, setup: true });
      }
      next = updateLongest(next, events);
      return { ok: true, state: next, events };
    }
    return fail("setup-only");
  }

  // --- Karty výzkumu (před hodem i po něm, jedna za tah) ---
  const playable = (kind: Dev) =>
    (s.phase === "roll" || s.phase === "main") && !s.devPlayedThisTurn && s.devHands[p].some((d) => d.kind === kind && d.boughtTurn < s.turnCount);
  const useDev = (st: KolonieState, kind: Dev): KolonieState => {
    const hand = st.devHands[p].slice();
    hand.splice(hand.findIndex((d) => d.kind === kind && d.boughtTurn < st.turnCount), 1);
    const devHands = st.devHands.slice();
    devHands[p] = hand;
    return { ...st, devHands, devPlayedThisTurn: true };
  };
  switch (action.type) {
    case "playKnight": {
      if (!playable("hlidka")) return fail("cannot-play");
      let next = useDev(s, "hlidka");
      const knights = next.knights.slice();
      knights[p]++;
      next = { ...next, knights, returnPhase: s.phase, phase: "robber" };
      events.push({ type: "devPlayed", player: p, card: "hlidka" });
      next = updateLargest(next, events);
      return { ok: true, state: next, events };
    }
    case "playRoadBuilding": {
      if (!playable("tunely")) return fail("cannot-play");
      const left = LIMITS.tunel - piecesUsed(s, p).tunel;
      const free = Math.min(2, left);
      let next = useDev(s, "tunely");
      events.push({ type: "devPlayed", player: p, card: "tunely" });
      if (free > 0 && legalRoads(next, p).length) next = { ...next, freeRoads: free, returnPhase: s.phase, phase: "roadBuilding" };
      return { ok: true, state: next, events };
    }
    case "playYearOfPlenty": {
      if (!playable("zasoby")) return fail("cannot-play");
      const res = action.res;
      if (!Array.isArray(res) || res.length !== 2 || !res.every((r) => RESOURCES.includes(r))) return fail("bad-resource");
      const bag: Bag = {};
      for (const r of res) bag[r] = (bag[r] ?? 0) + 1;
      if (!RESOURCES.every((r) => s.bank[r] >= (bag[r] ?? 0))) return fail("bank-empty");
      const next = gain(useDev(s, "zasoby"), p, bag);
      events.push({ type: "devPlayed", player: p, card: "zasoby", gains: bag });
      return { ok: true, state: checkWin(next, events), events };
    }
    case "playMonopoly": {
      if (!playable("monopol")) return fail("cannot-play");
      if (!RESOURCES.includes(action.res)) return fail("bad-resource");
      let next = useDev(s, "monopol");
      const taken: number[] = s.players.map(() => 0);
      s.players.forEach((_, o) => {
        if (o === p) return;
        const n = next.hands[o][action.res];
        if (n) {
          next = transfer(next, o, p, { [action.res]: n });
          taken[o] = n;
        }
      });
      events.push({ type: "devPlayed", player: p, card: "monopol", res: action.res, taken });
      return { ok: true, state: next, events };
    }
  }

  // --- Fáze tahu ---
  switch (s.phase) {
    case "roll": {
      if (action.type !== "roll") return fail("must-roll");
      const a = rand(s, 6);
      const b = rand(a.state, 6);
      const dice: [number, number] = [a.value + 1, b.value + 1];
      const sum = dice[0] + dice[1];
      let next: KolonieState = { ...b.state, dice };
      events.push({ type: "rolled", player: p, dice, sum });
      if (sum === 7) {
        const discard = next.hands.map((h) => (total(h) > 7 ? Math.floor(total(h) / 2) : 0));
        const any = discard.some((d) => d > 0);
        next = { ...next, discard, returnPhase: "main", phase: any ? "discard" : "robber" };
        if (any) events.push({ type: "discardNeeded", counts: discard });
      } else {
        next = produce({ ...next, phase: "main" }, sum, events);
      }
      return { ok: true, state: checkWin(next, events), events };
    }
    case "discard":
      return fail("waiting-for-discard");
    case "robber": {
      if (action.type !== "moveRobber") return fail("must-move-robber");
      if (!Number.isInteger(action.hex) || action.hex < 0 || action.hex >= T.hexes.length || action.hex === s.robber) return fail("bad-hex");
      let next: KolonieState = { ...s, robber: action.hex };
      events.push({ type: "robberMoved", player: p, hex: action.hex });
      const victims = [...new Set(T.hexes[action.hex].corners.map((c) => s.corners[c]?.owner).filter((o): o is number => o !== undefined && o !== p))].filter(
        (o) => total(s.hands[o]) > 0,
      );
      if (victims.length === 0) return { ok: true, state: afterRobber(next, events), events };
      if (victims.length === 1) {
        next = doSteal(next, victims[0], events);
        return { ok: true, state: afterRobber(next, events), events };
      }
      return { ok: true, state: { ...next, phase: "steal", stealFrom: victims }, events };
    }
    case "steal": {
      if (action.type !== "steal" || !s.stealFrom.includes(action.victim)) return fail("bad-victim");
      const next = doSteal(s, action.victim, events);
      return { ok: true, state: afterRobber(next, events), events };
    }
    case "roadBuilding": {
      if (action.type === "endTurn" || action.type === "cancelTrade") {
        // Zbylé tunely zdarma lze nevyužít.
        const next = { ...s, freeRoads: 0, phase: s.returnPhase ?? "main", returnPhase: null };
        events.push({ type: "roadBuildingDone", player: p });
        return { ok: true, state: next, events };
      }
      if (action.type !== "buildRoad" || !roadOk(s, p, action.edge)) return fail("bad-edge");
      const edges = s.edges.slice();
      edges[action.edge] = p;
      let next: KolonieState = { ...s, edges, freeRoads: s.freeRoads - 1 };
      events.push({ type: "built", player: p, kind: "tunel", edge: action.edge, free: true });
      next = updateLongest(next, events);
      const more = next.freeRoads > 0 && piecesUsed(next, p).tunel < LIMITS.tunel && legalRoads(next, p).length > 0;
      if (!more) next = { ...next, freeRoads: 0, phase: next.returnPhase ?? "main", returnPhase: null };
      return { ok: true, state: checkWin(next, events), events };
    }
    case "main":
      return mainAction(s, p, action, events);
  }
  return fail("bad-phase");
}

function mainAction(s: KolonieState, p: number, action: KolonieAction, events: GameEvent[]): R<KolonieState> {
  const used = piecesUsed(s, p);
  const done = (next: KolonieState): R<KolonieState> => ({ ok: true, state: checkWin(next, events), events });
  switch (action.type) {
    case "buildRoad": {
      if (!has(s.hands[p], COSTS.tunel) || used.tunel >= LIMITS.tunel || !roadOk(s, p, action.edge)) return fail("cannot-build");
      const edges = s.edges.slice();
      edges[action.edge] = p;
      let next = pay({ ...s, edges }, p, COSTS.tunel);
      events.push({ type: "built", player: p, kind: "tunel", edge: action.edge });
      next = updateLongest(next, events);
      return done(next);
    }
    case "buildSettlement": {
      if (!has(s.hands[p], COSTS.zakladna) || used.zakladna >= LIMITS.zakladna || !settlementOk(s, p, action.corner)) return fail("cannot-build");
      const corners = s.corners.slice();
      corners[action.corner] = { owner: p, city: false };
      let next = pay({ ...s, corners }, p, COSTS.zakladna);
      events.push({ type: "built", player: p, kind: "zakladna", corner: action.corner });
      next = updateLongest(next, events);
      return done(next);
    }
    case "buildCity": {
      const b = s.corners[action.corner];
      if (!has(s.hands[p], COSTS.mesto) || used.mesto >= LIMITS.mesto || !b || b.owner !== p || b.city) return fail("cannot-build");
      const corners = s.corners.slice();
      corners[action.corner] = { owner: p, city: true };
      const next = pay({ ...s, corners }, p, COSTS.mesto);
      events.push({ type: "built", player: p, kind: "mesto", corner: action.corner });
      return done(next);
    }
    case "buyDev": {
      if (!has(s.hands[p], COSTS.vyzkum) || !s.devDeck.length) return fail("cannot-buy");
      const deck = s.devDeck.slice();
      const card = deck.pop()!;
      const devHands = s.devHands.slice();
      devHands[p] = [...devHands[p], { kind: card, boughtTurn: s.turnCount }];
      const next = pay({ ...s, devDeck: deck, devHands }, p, COSTS.vyzkum);
      events.push({ type: "devBought", player: p });
      events.push({ type: "devCard", to: [s.players[p].id], card });
      return done(next);
    }
    case "bankTrade": {
      const { give, get } = action;
      if (!RESOURCES.includes(give) || !RESOURCES.includes(get) || give === get) return fail("bad-trade");
      const rate = bankRate(s, p, give);
      if (s.hands[p][give] < rate || s.bank[get] < 1) return fail("cannot-trade");
      const next = gain(pay(s, p, { [give]: rate }), p, { [get]: 1 });
      events.push({ type: "bankTraded", player: p, give, rate, get });
      return done(next);
    }
    case "offerTrade": {
      const { give, want } = action;
      if (!validBag(give) || !validBag(want) || !total(give) || !total(want)) return fail("bad-offer");
      if (RESOURCES.some((r) => (give[r] ?? 0) > 0 && (want[r] ?? 0) > 0)) return fail("same-resource");
      if (!has(s.hands[p], give)) return fail("cannot-afford");
      const to = action.to;
      if (to !== null && (!Number.isInteger(to) || to === p || to < 0 || to >= s.players.length)) return fail("bad-target");
      const trade: TradeOffer = { id: s.nextOfferId, from: p, give, want, to, responses: {} };
      events.push({ type: "tradeOffered", offer: trade });
      return { ok: true, state: { ...s, trade, nextOfferId: s.nextOfferId + 1 }, events };
    }
    case "confirmTrade": {
      const t = s.trade;
      if (!t || t.id !== action.offerId) return fail("no-offer");
      const r = t.responses[action.with];
      if (!r || r.kind === "decline") return fail("not-accepted");
      const give = r.kind === "counter" ? r.give : t.give;
      const want = r.kind === "counter" ? r.want : t.want;
      if (!has(s.hands[p], give) || !has(s.hands[action.with], want)) return fail("cannot-afford");
      const next = transfer(transfer({ ...s, trade: null }, p, action.with, give), action.with, p, want);
      events.push({ type: "tradeDone", player: p, with: action.with, give, want });
      return done(next);
    }
    case "cancelTrade": {
      if (!s.trade) return fail("no-offer");
      events.push({ type: "tradeCancelled", offerId: s.trade.id });
      return { ok: true, state: { ...s, trade: null }, events };
    }
    case "endTurn": {
      const turn = (s.turn + 1) % s.players.length;
      let next: KolonieState = { ...s, turn, turnCount: s.turnCount + 1, phase: "roll", dice: null, trade: null, devPlayedThisTurn: false };
      events.push({ type: "turn", player: turn });
      // Body mohly přibýt mimo tah (např. přerušená cizí cesta); výhra se hlásí na začátku vlastního tahu.
      next = checkWin(next, events);
      return { ok: true, state: next, events };
    }
  }
  return fail("bad-action");
}

// ---------- Pohled, čekání, boti ----------

export function pending(s: KolonieState): Pending[] {
  const id = (p: number) => s.players[p].id;
  const cur = id(s.turn);
  switch (s.phase) {
    case "setup":
      return [{ player: cur, kind: s.setup!.placing === "settlement" ? "setupSettlement" : "setupRoad" }];
    case "roll":
      return [{ player: cur, kind: "roll" }];
    case "discard":
      return s.discard.flatMap((n, p): Pending[] => (n > 0 ? [{ player: id(p), kind: "discard", count: n }] : []));
    case "robber":
      return [{ player: cur, kind: "robber" }];
    case "steal":
      return [{ player: cur, kind: "steal" }];
    case "roadBuilding":
      return [{ player: cur, kind: "roadBuilding", count: s.freeRoads }];
    case "main": {
      const list: Pending[] = [{ player: cur, kind: "main" }];
      const t = s.trade;
      if (t) {
        s.players.forEach((_, p) => {
          if (p === s.turn || (t.to !== null && t.to !== p) || t.responses[p]) return;
          list.push({ player: id(p), kind: "tradeResponse", offerId: t.id });
        });
      }
      return list;
    }
  }
  return [];
}

function viewFor(s: KolonieState, playerId: string | null): KolonieView {
  const me = playerId === null ? -1 : s.players.findIndex((p) => p.id === playerId);
  return {
    version: s.version,
    hexes: s.hexes,
    ports: s.ports,
    robber: s.robber,
    corners: s.corners,
    edges: s.edges,
    players: s.players.map((pl, p) => {
      const used = piecesUsed(s, p);
      return {
        id: pl.id,
        name: pl.name,
        isBot: !!pl.isBot,
        cards: total(s.hands[p]),
        devCards: s.devHands[p].length,
        knights: s.knights[p],
        publicPoints: points(s, p, s.phase === "over"),
        longestRoad: s.roadLengths[p],
        pieces: { tunel: LIMITS.tunel - used.tunel, zakladna: LIMITS.zakladna - used.zakladna, mesto: LIMITS.mesto - used.mesto },
      };
    }),
    turn: s.turn,
    phase: s.phase,
    dice: s.dice,
    bank: s.bank,
    devDeck: s.devDeck.length,
    longestHolder: s.longestHolder,
    largestHolder: s.largestHolder,
    trade: s.trade,
    pending: pending(s),
    targetPoints: s.targetPoints,
    winner: s.winner,
    me: me >= 0 ? me : null,
    hand: me >= 0 ? { ...s.hands[me] } : null,
    devHand: me >= 0 ? s.devHands[me].map((d) => ({ kind: d.kind, fresh: d.boughtTurn >= s.turnCount })) : [],
    myPoints: me >= 0 ? points(s, me) : null,
    discardNeeded: me >= 0 ? s.discard[me] : 0,
    stealFrom: me === s.turn ? s.stealFrom : [],
    freeRoads: me === s.turn ? s.freeRoads : 0,
    devPlayedThisTurn: s.devPlayedThisTurn,
    turnCount: s.turnCount,
  };
}

/** Všechny legální akce hráče (pro boty a pro náhodné testy pravidel). Obchodní nabídky jen vzorek 1:1. */
function legalActions(s: KolonieState, playerId: string): KolonieAction[] {
  const p = s.players.findIndex((pl) => pl.id === playerId);
  if (p < 0 || s.phase === "over") return [];
  const out: KolonieAction[] = [];
  const hand = s.hands[p];
  if (s.phase === "discard" && s.discard[p] > 0) {
    // Kanonické odhození: z nejpočetnějších surovin.
    const bag: Bag = {};
    const h = { ...hand };
    for (let i = 0; i < s.discard[p]; i++) {
      const r = RESOURCES.reduce((a, b) => (h[b] > h[a] ? b : a));
      h[r]--;
      bag[r] = (bag[r] ?? 0) + 1;
    }
    out.push({ type: "discard", cards: bag });
  }
  if (s.phase === "main" && s.trade && p !== s.turn && (s.trade.to === null || s.trade.to === p) && !s.trade.responses[p]) {
    if (has(hand, s.trade.want)) out.push({ type: "respondTrade", offerId: s.trade.id, response: "accept" });
    out.push({ type: "respondTrade", offerId: s.trade.id, response: "decline" });
  }
  if (p !== s.turn) return out;

  switch (s.phase) {
    case "setup":
      if (s.setup!.placing === "settlement") for (const c of legalSetupSettlements(s)) out.push({ type: "placeSettlement", corner: c });
      else for (const e of legalSetupRoads(s)) out.push({ type: "placeRoad", edge: e });
      return out;
    case "robber":
      T.hexes.forEach((_, h) => h !== s.robber && out.push({ type: "moveRobber", hex: h }));
      return out;
    case "steal":
      for (const v of s.stealFrom) out.push({ type: "steal", victim: v });
      return out;
    case "roadBuilding":
      for (const e of legalRoads(s, p)) out.push({ type: "buildRoad", edge: e });
      out.push({ type: "endTurn" });
      return out;
    case "discard":
      return out;
  }
  const devOk = (kind: Dev) => !s.devPlayedThisTurn && s.devHands[p].some((d) => d.kind === kind && d.boughtTurn < s.turnCount);
  if (devOk("hlidka")) out.push({ type: "playKnight" });
  if (devOk("tunely")) out.push({ type: "playRoadBuilding" });
  if (devOk("monopol")) for (const r of RESOURCES) out.push({ type: "playMonopoly", res: r });
  if (devOk("zasoby"))
    for (const a of RESOURCES) for (const b of RESOURCES) if (a <= b && s.bank[a] >= (a === b ? 2 : 1) && s.bank[b] >= 1) out.push({ type: "playYearOfPlenty", res: [a, b] });
  if (s.phase === "roll") {
    out.push({ type: "roll" });
    return out;
  }
  // main
  const used = piecesUsed(s, p);
  if (has(hand, COSTS.tunel) && used.tunel < LIMITS.tunel) for (const e of legalRoads(s, p)) out.push({ type: "buildRoad", edge: e });
  if (has(hand, COSTS.zakladna) && used.zakladna < LIMITS.zakladna) for (const c of legalSettlements(s, p)) out.push({ type: "buildSettlement", corner: c });
  if (has(hand, COSTS.mesto) && used.mesto < LIMITS.mesto)
    s.corners.forEach((b, c) => b && b.owner === p && !b.city && out.push({ type: "buildCity", corner: c }));
  if (has(hand, COSTS.vyzkum) && s.devDeck.length) out.push({ type: "buyDev" });
  for (const give of RESOURCES)
    if (hand[give] >= bankRate(s, p, give)) for (const get of RESOURCES) if (get !== give && s.bank[get] > 0) out.push({ type: "bankTrade", give, get });
  if (s.trade) {
    for (const [o, r] of Object.entries(s.trade.responses)) {
      if (r.kind === "decline") continue;
      // Potvrdit jde jen obchod, na který obě strany pořád mají (mezitím mohly suroviny utratit).
      const give = r.kind === "counter" ? r.give : s.trade.give;
      const want = r.kind === "counter" ? r.want : s.trade.want;
      if (has(hand, give) && has(s.hands[Number(o)], want)) out.push({ type: "confirmTrade", offerId: s.trade.id, with: Number(o) });
    }
    out.push({ type: "cancelTrade" });
  } else {
    for (const give of RESOURCES) if (hand[give] > 0) for (const want of RESOURCES) if (want !== give) out.push({ type: "offerTrade", give: { [give]: 1 }, want: { [want]: 1 }, to: null });
  }
  out.push({ type: "endTurn" });
  return out;
}

export const kolonie: GameModule<KolonieState, KolonieAction> = {
  id: "kolonie",
  name: "Kolonie na Keplerovi",
  minPlayers: 3,
  maxPlayers: 4,
  options: [
    {
      key: "seats",
      label: "Hráčů celkem",
      choices: [
        { value: "4", label: "4 hráči, prázdná místa doplní roboti" },
        { value: "3", label: "3 hráči, prázdná místa doplní roboti" },
      ],
      default: "4",
    },
    {
      key: "points",
      label: "Body k vítězství",
      choices: [
        { value: "10", label: "10 bodů, standardní hra" },
        { value: "8", label: "8 bodů, kratší hra" },
      ],
      default: "10",
    },
  ],
  init,
  applyAction,
  viewFor,
  isOver: (s) => s.phase === "over",
  currentActors: (s) => [...new Set(pending(s).map((x) => x.player))],
  legalActions,
  bot: (view, memory, opts) => botAction(view as KolonieView, memory as BotMemory, opts),
};
